import style from '../../components/NevisPasskeysSwissRenamePopup/NevisPasskeysSwissRenamePopup.scss';
import { useCallback } from 'react';
import { useUserSelector } from '@core/js/store/hooks';
import { pick } from 'ramda';
import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { useDispatch } from 'react-redux';
import { INevisCreatePassKeysProps } from '@theme/js/components/main/NevisPasskeysSwiss/types';
import { showNevisPopupAction } from '../../actions';
import { CommonPopup } from '@core/js/components/common/popups/CommonPopup';

declare global {
    interface Window {
        fidoAuth?: {
            isSupported: () => void;
            ERR_NOT_SUPPORTED: string;
            enroll: (enrollCallback: (response: any) =>
                Promise<void>, passkeyName: string, userName: string | undefined, sessionToken: string | undefined) =>
                Promise<void>;
        };
        iapiERR_OK: string;
    }
}

export const useCreatePasskey = ({
    setDisabledState,
    data,
    fetchPortlet,
    portletType,
    tkPrefix
} : INevisCreatePassKeysProps) => {
    const { userName, sessionToken } = useUserSelector(pick(['userName', 'sessionToken']));
    const isSupportedBrowser = window.fidoAuth?.isSupported();
    const showNotification = useShowNotification();
    const dispatch = useDispatch();

    const enrollCallback = useCallback(async (response: any) => {
        if (response.errorCode === window.iapiERR_OK) {
            await fetchPortlet({ type: portletType }, { showLoader: false });
            window.localStorage.setItem('hasPasskeys', String(true));
        } else if (response?.userErrors && response?.userErrors?.UserError?.[0]?.templateId === 'fido2_too_many_authenticators') {
            showNotification(response?.userErrors?.UserError?.[0]?.message, { variant : 'error' });
        } else if (response.errorCode === window.fidoAuth?.ERR_NOT_SUPPORTED) {
            showNotification(`${tkPrefix}passkeys.error.notSupported`, { variant: 'error' });
            console.warn(response);
        } else {
            console.error(response);
        }
    }, [fetchPortlet, portletType, showNotification, tkPrefix]);

    return useCallback(async () => {
        if (typeof data === 'undefined') {
            dispatch(showNevisPopupAction({
                className: style.revokePopupContainer,
                title: `${tkPrefix}passkeys.message`,
                content: `${tkPrefix}passkeys.error.notWork`
            }, CommonPopup));
        } else {
            if (isSupportedBrowser) {
                setDisabledState(true);
                const passKeyValueName = data?.fidoAuthenticators ? data.fidoAuthenticators.length + 1 : 0;
                const passkeyName = `Passkey #${passKeyValueName}`;
                await window.fidoAuth?.enroll(enrollCallback, passkeyName, userName, sessionToken)
                    .then(() => console.log('Enrollment initiated'))
                    .catch(() => console.error('Could not initiate enrollment'))
                    .finally(() => setDisabledState(false));
            } else {
                showNotification(`${tkPrefix}passkeys.notSupported`, { variant: 'error' });
            }
        }
    }, [
        data, dispatch, enrollCallback,
        isSupportedBrowser, sessionToken, setDisabledState,
        showNotification, tkPrefix, userName
    ]);
};
